/**
 * Random number generator that works across modern JavaScript environments.
 *
 * This implementation uses the Web Crypto API which is available in:
 * - Node.js 18+ via globalThis.crypto
 * - Modern browsers via globalThis.crypto, self.crypto, or window.crypto
 * - Web Workers and Service Workers via self.crypto
 * - Deno and Bun via globalThis.crypto
 * - Node.js 22+ via process.getBuiltinModule when Web Crypto is unavailable
 *
 * @throws {Error} If no secure random number generator is available
 */
class Rand {
    _rand; // ✅ Explicit function type
    getRandomValues(obj, n) {
        const arr = new Uint8Array(n);
        obj.crypto.getRandomValues(arr);
        return Array.from(arr);
    }
    constructor() {
        const noRand = () => {
            throw new Error('No secure random number generator is available in this environment.');
        };
        this._rand = noRand; // Assign the function
        // Try globalThis.crypto (works in Node.js 18+, modern browsers, and Deno)
        if (typeof globalThis !== 'undefined' && typeof globalThis.crypto?.getRandomValues === 'function') {
            this._rand = (n) => {
                return this.getRandomValues(globalThis, n);
            };
            return;
        }
        // Try self.crypto (Web Workers and Service Workers)
        if (globalThis.self !== undefined && typeof globalThis.self.crypto?.getRandomValues === 'function') {
            this._rand = (n) => {
                return this.getRandomValues(globalThis.self, n);
            };
            return;
        }
        // Try window.crypto (browsers)
        if (globalThis.window !== undefined && typeof globalThis.window.crypto?.getRandomValues === 'function') {
            this._rand = (n) => {
                return this.getRandomValues(globalThis.window, n);
            };
            return;
        }
        // Retain a Node.js fallback without a static import or require that browser
        // and mobile bundlers would have to resolve.
        if (typeof process !== 'undefined' &&
            typeof process.getBuiltinModule === 'function') {
            const nodeCrypto = process.getBuiltinModule('node:crypto');
            if (typeof nodeCrypto?.randomBytes === 'function') {
                this._rand = n => Array.from(nodeCrypto.randomBytes(n));
                return;
            }
        }
        // No crypto available
        this._rand = noRand;
    }
    generate(len) {
        return this._rand(len);
    }
}
let ayn = null;
/**
 * Generates a sequence of pseudo-random bytes with the given length.
 *
 * @param len - The number of bytes to generate
 *
 * @returns The generated bytes
 *
 * @example
 * import Random from '@bsv/sdk/primitives/Random'
 * const bytes = Random(32) // Produces 32 random bytes
 */
const Random = (len) => {
    ayn ??= new Rand();
    return ayn.generate(len);
};
export default Random;
//# sourceMappingURL=Random.js.map